// STUDIO-BR-TX3-TIMER-NOTICE-CONTROL-1 · 리모컨 공지·타이머 메뉴 (TEST · 기존 공유 상태 REUSE).
import React from 'react';
import { Camera, Clock, Images, LayoutList, LogOut, Megaphone, MonitorUp } from 'lucide-react';
import { MENU_ROOT } from '@/lib/remoteControl';
import { noticeType } from '@/lib/testLiveShare';

export const ROOT_LABEL = { leave: '나가기', quick: '빠른등록', product: '상품 제어로 복귀', past: '지난 상품 슬라이드', timer: '타이머', notice: '자사라이브 공지', ext: '외부송출용 공지' };
const ROOT_ICON = { leave: LogOut, product: LayoutList, quick: Camera, past: Images, timer: Clock, notice: Megaphone, ext: MonitorUp };
const TIMER_STATUS = { running: '진행 중', paused: '일시정지', finished: '종료', idle: '대기' };
const extLabel = (n) => (noticeType(n) === 'image' ? '이미지 공지' : `텍스트 · ${n.text}`);

// 하위 메뉴 항목 (마지막 항목은 정지·내리기·해제 같은 제어 행).
export const menuRows = (level, { ctx, library, past }) => {
  if (level === 'past') {
    const on = past?.mode && past.mode !== 'off';
    return [
      { kind: 'toggle', label: on ? '슬라이드 끄기 (현재 ON)' : '슬라이드 켜기 (현재 OFF)', on },
      { kind: 'play', label: past?.playing ? '자동 재생 정지' : '자동 재생 시작', on: !!past?.playing },
      { kind: 'prev', label: '이전 슬라이드' },
      { kind: 'next', label: '다음 슬라이드' },
      ...(past?.list || []).map((p, i) => ({ kind: 'pick', i, label: `${i + 1}. ${p.name || '상품'}`, on: !!past?.playing && past.index === i })),
    ];
  }
  if (level === 'timer') {
    const st = ctx?.timer?.status;
    return [
      ...(ctx?.timer_presets || []).map((p) => ({ kind: 'preset', p, label: `${p.label} · ${p.minutes}분`, on: st === 'running' && ctx?.timer?.label === p.label })),
      { kind: 'pause', label: st === 'paused' ? '재개' : '일시정지' },
      { kind: 'stop', label: '정지' },
    ];
  }
  if (level === 'notice') {
    const cur = ctx?.live_notice?.notice_id;
    return [
      ...(library || []).filter((n) => (n.text || '').trim()).map((n) => ({ kind: 'show', n, label: n.text, on: cur === n.notice_id })),
      { kind: 'clear', label: '현재 공지 내리기' },
    ];
  }
  if (level === 'ext') {
    const sel = ctx?.external_notice_selected;
    return [
      ...(ctx?.external_notices || []).map((n) => ({ kind: 'sel', n, label: extLabel(n), on: sel === n.id })),
      { kind: 'unsel', label: '선택 해제' },
    ];
  }
  return [];
};

const rootStatus = (key, ctx, past) => {
  if (key === 'leave') return '메뉴·목록 닫고 방송 화면으로';
  if (key === 'quick') return '3초 후 캡처';
  if (key === 'past') {
    if (!past || past.mode === 'off') return `OFF · 지난 상품 ${past?.list?.length || 0}개`;
    return `${past.playing ? `재생 중 ${past.index + 1}/${past.list.length}` : 'ON · 정지'} · 지난 상품 ${past.list.length}개`;
  }
  if (key === 'timer') return ctx?.timer?.status && ctx.timer.status !== 'idle' ? `${TIMER_STATUS[ctx.timer.status] || ''}${ctx.timer.label ? ` · ${ctx.timer.label}` : ''}` : '대기';
  if (key === 'notice') return ctx?.live_notice?.text ? `표시 중 · ${ctx.live_notice.text}` : '표시 중인 공지 없음';
  if (key === 'ext') {
    const n = (ctx?.external_notices || []).find((x) => x.id === ctx?.external_notice_selected);
    return n ? `선택됨 · ${extLabel(n)}` : '선택 없음';
  }
  return '';
};

const ON_LABEL = { timer: '실행 중', ext: '선택됨', past: '켜짐' };

export const RemoteMenuPanel = ({ menu, ctx, library, past }) => {
  const rows = menu.level === 'root'
    ? MENU_ROOT.map((k) => ({ kind: k, label: ROOT_LABEL[k], sub: rootStatus(k, ctx, past), Icon: ROOT_ICON[k] }))
    : menuRows(menu.level, { ctx, library, past });
  return (
    <div className="fixed left-3 right-[4.5rem] bottom-28 z-[71] max-w-xs rounded-2xl bg-black/85 backdrop-blur-md p-3 text-white shadow-xl"
         data-testid="remote-menu" data-level={menu.level}>
      <p className="text-[11px] font-black text-white/70" data-testid="remote-menu-title">
        통합 제어 메뉴{menu.level !== 'root' ? ` › ${ROOT_LABEL[menu.level]}` : ''}
      </p>
      <div className="mt-2 space-y-1 max-h-[35vh] overflow-y-auto">
        {menu.level !== 'root' && menu.level !== 'past' && rows.length === 1 && <p className="text-[11px] text-white/50 px-1">저장된 항목이 없어요.</p>}
        {rows.map((r, i) => {
          const cur = i === menu.idx;
          return (
            <div key={`${r.kind}-${i}`}
                 className={`rounded-lg px-2.5 py-1.5 border ${cur ? 'border-[#EF4444] bg-[#EF4444]/20' : 'border-white/10'}`}
                 data-testid={`remote-menu-row-${i}`} data-cursor={cur ? '1' : '0'} data-on={r.on ? '1' : '0'}>
              <div className="flex items-center gap-2">
                {r.Icon && <r.Icon size={14} className="shrink-0 text-white/80" aria-hidden="true" data-testid={`remote-menu-row-${i}-icon`} />}
                <span className="flex-1 min-w-0 text-xs font-black truncate">{r.label}</span>
                {r.on && <span className="shrink-0 text-[10px] font-black text-[#30D158]">{ON_LABEL[menu.level] || '표시 중'}</span>}
              </div>
              {r.sub && <p className={`text-[10px] text-white/60 truncate ${r.Icon ? 'pl-[22px]' : ''}`} data-testid={`remote-menu-row-${i}-status`}>{r.sub}</p>}
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-[10px] text-white/50">↑↓ 이동 · 중앙 실행 · {menu.level === 'root' ? '→ 진입 · ← 상품 제어' : '← 뒤로'}</p>
    </div>
  );
};

export default RemoteMenuPanel;
